<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable no-use-before-define */
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisMessageTemplatesAPI from 'dashboard/api/laisMessageTemplates';
import InboxesAPI from 'dashboard/api/inboxes';

const store = useStore();

const copy = Object.freeze({
  pageTitle: 'Templates',
  pageDescription:
    'Visualize os templates sincronizados do WhatsApp e gerencie os modelos operacionais usados na operação.',
  loading: 'Carregando templates...',
  searchPlaceholder: 'Buscar templates',
  syncedTitle: 'Templates sincronizados do WhatsApp',
  syncedDescription:
    'Use esta seção para visualizar, criar e acompanhar os templates diretamente na API do Facebook/WhatsApp.',
  syncedEmpty:
    'Nenhum inbox de WhatsApp foi encontrado nesta conta. Conecte um inbox para visualizar os templates sincronizados.',
  syncedNoTemplates:
    'Nenhum template sincronizado foi encontrado para o inbox selecionado.',
  syncedSearchPlaceholder: 'Buscar template sincronizado',
  syncedInboxLabel: 'Inbox do WhatsApp',
  syncedSyncButton: 'Atualizar da API do Facebook',
  syncedSyncSuccess: 'Templates atualizados pela API do Facebook.',
  syncedLoading: 'Atualizando templates no Facebook...',
  syncedLiveStatus: 'Status atualizado automaticamente a cada 15 segundos.',
  facebookCreateButton: 'Criar template no Facebook',
  facebookCreateTitle: 'Novo template no Facebook',
  facebookCreateSuccess:
    'Template enviado para análise da Meta. O status será atualizado automaticamente.',
  facebookCreateError: 'Não foi possível criar o template no Facebook.',
  facebookNameHelper:
    'Use somente letras minúsculas, números e underline. Ex.: follow_up_venda_01.',
  facebookBodyHelper:
    'Clique em uma variável para inseri-la no ponto onde o cursor estiver neste conteúdo.',
  facebookVariablesTitle: 'Variáveis disponíveis nos públicos',
  facebookVariablesHelper:
    'Os campos personalizados importados nos públicos aparecem aqui como parâmetros nomeados da Meta.',
  facebookVariablesEmpty:
    'Nenhum campo personalizado foi encontrado nos públicos desta conta.',
  facebookVariableAvailableIn: 'Disponível nos públicos',
  facebookInterviewVariablesTitle: 'Variáveis dinâmicas de entrevista',
  facebookInterviewVariablesHelper:
    'Clique para inserir o parâmetro no template. Ao criar a campanha, use o código dinâmico exibido em cada item.',
  facebookCampaignVariableLabel: 'Na campanha',
  facebookCategoryLabel: 'Categoria Meta',
  facebookLanguageLabel: 'Idioma Meta',
  facebookHeaderLabel: 'Cabeçalho em texto',
  facebookFooterLabel: 'Rodapé',
  facebookOptionalLabel: 'Opcional',
  facebookSubmitButton: 'Enviar para análise',
  operationalTitle: 'Modelos operacionais',
  operationalDescription:
    'Crie e mantenha os modelos internos usados pela operação em remarketing, atualização e follow-up.',
  operationalEmpty:
    'Nenhum modelo operacional cadastrado ainda. Crie o primeiro template para começar.',
  operationalSearchPlaceholder: 'Buscar modelo operacional',
  newButton: 'Novo template',
  editButton: 'Editar',
  deleteButton: 'Excluir',
  active: 'Ativo',
  inactive: 'Inativo',
  categoryAll: 'Todas as categorias',
  statusAll: 'Todos os status',
  statusActive: 'Somente ativos',
  statusInactive: 'Somente inativos',
  modalCreateTitle: 'Novo template operacional',
  modalEditTitle: 'Editar template operacional',
  nameLabel: 'Nome',
  categoryLabel: 'Categoria',
  contentLabel: 'Conteúdo',
  activeLabel: 'Template ativo',
  contentHelper:
    'Use placeholders como {{nome}} ou {{imovel}}. As variáveis são detectadas automaticamente.',
  variablesLabel: 'Variáveis detectadas',
  variablesEmpty: 'Nenhuma variável detectada neste conteúdo.',
  saveButton: 'Salvar template',
  cancelButton: 'Cancelar',
  createSuccess: 'Template criado com sucesso.',
  updateSuccess: 'Template atualizado com sucesso.',
  deleteTitle: 'Excluir template',
  deleteDescription:
    'Tem certeza que deseja excluir este template? Esta ação não pode ser desfeita.',
  deleteConfirm: 'Excluir template',
  deleteSuccess: 'Template removido com sucesso.',
  loadError: 'Não foi possível carregar os templates.',
  saveError: 'Não foi possível salvar o template.',
  deleteError: 'Não foi possível excluir o template.',
  syncedPreviewFallback: 'Sem conteúdo de visualização.',
  modelCountLabel: 'modelo(s)',
  headerLabel: 'Header',
  bodyLabel: 'Body',
  footerLabel: 'Footer',
  buttonsLabel: 'Buttons',
  separator: '·',
  metadataLanguage: 'Idioma',
  metadataCategory: 'Categoria',
  metadataStatus: 'Status',
  metadataUpdated: 'Atualizado em',
  sectionFilterLabel: 'Categoria',
  sectionStatusLabel: 'Status',
});

const categoryOptions = Object.freeze([
  { value: 'all', label: copy.categoryAll },
  { value: 'remarketing', label: 'Remarketing' },
  { value: 'atualizacao', label: 'Atualização' },
  { value: 'saudacao', label: 'Saudação' },
  { value: 'followup', label: 'Follow-up' },
  { value: 'personalizada', label: 'Personalizada' },
  { value: 'lancamento', label: 'Lançamento' },
]);

const statusOptions = Object.freeze([
  { value: 'all', label: copy.statusAll },
  { value: 'active', label: copy.statusActive },
  { value: 'inactive', label: copy.statusInactive },
]);

const facebookCategoryOptions = Object.freeze([
  { value: 'UTILITY', label: 'Utility' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
]);

const facebookLanguageOptions = Object.freeze([
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'es_ES', label: 'Español' },
]);

const baseFacebookVariables = Object.freeze([
  { key: 'nome', label: 'Nome', audiences: [] },
  { key: 'primeiro_nome', label: 'Primeiro nome', audiences: [] },
  { key: 'ultimo_nome', label: 'Último nome', audiences: [] },
  { key: 'email', label: 'E-mail', audiences: [] },
  { key: 'telefone', label: 'Telefone', audiences: [] },
]);

const interviewFacebookVariables = Object.freeze([
  {
    key: 'nome_contato',
    label: 'Nome do contato',
    token: '{{nome_contato}}',
    campaignValue: '{{ contato.nome }}',
  },
  {
    key: 'proxima_data',
    label: 'Somente data',
    token: '{{proxima_data}}',
    campaignValue: '{{ interview.proxima_data }}',
  },
  {
    key: 'proxima_horario',
    label: 'Somente horário',
    token: '{{proxima_horario}}',
    campaignValue: '{{ interview.proxima_horario }}',
  },
  {
    key: 'proxima_data_hora_extenso',
    label: 'Data e horário com período',
    token: '{{proxima_data_hora_extenso}}',
    campaignValue: '{{ interview.proxima_data_hora_extenso }}',
  },
]);

const isLoading = ref(true);
const isSaving = ref(false);
const isDeleting = ref(false);
const isSyncing = ref(false);
const isCreatingFacebookTemplate = ref(false);
const isLoadingFacebookTemplates = ref(false);
const templates = ref([]);
const facebookTemplates = ref([]);
const hasLoadedFacebookTemplates = ref(false);
const operationalSearch = ref('');
const syncedSearch = ref('');
const selectedCategory = ref('all');
const selectedStatus = ref('all');
const selectedInboxId = ref('');
const showFormModal = ref(false);
const showFacebookFormModal = ref(false);
const showDeleteModal = ref(false);
const editingTemplateId = ref(null);
const deletingTemplate = ref(null);
const form = ref(buildDefaultForm());
const facebookForm = ref(buildDefaultFacebookForm());
const facebookBodyInput = ref(null);
const facebookBodySelection = ref({ start: 0, end: 0 });
let templateStatusPollingTimer = null;

function buildDefaultForm() {
  return {
    nome: '',
    categoria: 'remarketing',
    conteudo: '',
    ativo: true,
  };
}

function buildDefaultFacebookForm() {
  return {
    name: '',
    category: 'UTILITY',
    language: 'pt_BR',
    header: '',
    body: '',
    footer: '',
  };
}

const inboxRecords = computed(() => store.getters['inboxes/getInboxes'] || []);

const audienceLists = computed(
  () => store.getters['audienceLists/getAudienceLists'] || []
);

function facebookVariableKey(field) {
  const key = String(field || '').trim();
  if (!/^[a-z0-9_]+$/.test(key)) return '';

  return /^[a-z]/.test(key) ? key : `campo_${key}`;
}

const facebookVariables = computed(() => {
  const variables = new Map(
    baseFacebookVariables.map(variable => [variable.key, { ...variable }])
  );

  audienceLists.value.forEach(audience => {
    (Array.isArray(audience.custom_fields)
      ? audience.custom_fields
      : []
    ).forEach(field => {
      const fieldName = String(field || '').trim();
      const key = facebookVariableKey(fieldName);
      if (!key) return;

      const existing = variables.get(key);
      if (existing) {
        if (audience.name && !existing.audiences.includes(audience.name)) {
          existing.audiences.push(audience.name);
        }
        return;
      }

      variables.set(key, {
        key,
        label: fieldName
          .replace(/_/g, ' ')
          .replace(/\b\w/g, character => character.toUpperCase()),
        audiences: audience.name ? [audience.name] : [],
      });
    });
  });

  return Array.from(variables.values()).map(variable => ({
    ...variable,
    token: `{{${variable.key}}}`,
  }));
});

const whatsappInboxes = computed(() =>
  inboxRecords.value.filter(inbox => {
    if (inbox.channel_type === INBOX_TYPES.WHATSAPP) return true;
    return (
      inbox.channel_type === INBOX_TYPES.TWILIO && inbox.medium === 'whatsapp'
    );
  })
);

const syncedTemplates = computed(() => {
  if (hasLoadedFacebookTemplates.value) return facebookTemplates.value;
  if (!selectedInboxId.value) return [];

  const cachedTemplates =
    store.getters['inboxes/getWhatsAppTemplates'](selectedInboxId.value) || [];
  return Array.isArray(cachedTemplates) ? cachedTemplates : [];
});

const filteredSyncedTemplates = computed(() => {
  const query = syncedSearch.value.trim().toLowerCase();

  return syncedTemplates.value.filter(template => {
    if (!query) return true;

    const haystack = [
      template.name,
      template.category,
      template.language,
      template.status,
      componentText(template, 'BODY'),
      componentText(template, 'HEADER'),
      componentText(template, 'FOOTER'),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(query);
  });
});

const filteredOperationalTemplates = computed(() => {
  const query = operationalSearch.value.trim().toLowerCase();

  return templates.value.filter(template => {
    if (selectedCategory.value !== 'all') {
      if (template.categoria !== selectedCategory.value) return false;
    }

    if (selectedStatus.value === 'active' && !template.ativo) return false;
    if (selectedStatus.value === 'inactive' && template.ativo) return false;

    if (!query) return true;

    const haystack = [template.nome, template.categoria, template.conteudo]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(query);
  });
});

const formVariables = computed(() => extractVariableKeys(form.value.conteudo));

function extractVariableKeys(content) {
  return Array.from(
    new Set(
      Array.from(String(content || '').matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)).map(
        match => match[1]
      )
    )
  );
}

function variablesMapFromContent(content, previousValues = {}) {
  return extractVariableKeys(content).reduce((result, key) => {
    result[key] = previousValues[key] || '';
    return result;
  }, {});
}

function normalizeTemplate(record) {
  return {
    id: record.id,
    nome: record.nome,
    categoria: record.categoria,
    conteudo: record.conteudo,
    ativo: Boolean(record.ativo),
    variaveis: record.variaveis || {},
    createdAt: record.created_at,
    updatedAt: record.updated_at,
  };
}

function componentByType(template, type) {
  return (template.components || []).find(component => component.type === type);
}

function componentText(template, type) {
  const component = componentByType(template, type);
  if (!component) return '';

  if (component.text) return component.text;
  if (component.format) return component.format;

  return '';
}

function buttonLabels(template) {
  const buttons = componentByType(template, 'BUTTONS')?.buttons || [];
  return buttons.map(button => button.text).filter(Boolean);
}

function templateStatusClass(status) {
  switch (String(status || '').toUpperCase()) {
    case 'APPROVED':
      return 'bg-emerald-50 text-emerald-700';
    case 'REJECTED':
    case 'DISABLED':
      return 'bg-red-50 text-red-700';
    case 'PAUSED':
      return 'bg-slate-100 text-slate-700';
    default:
      return 'bg-amber-50 text-amber-700';
  }
}

function formatDate(value) {
  if (!value) return '—';

  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function metadataText(...parts) {
  return parts.filter(Boolean).join(` ${copy.separator} `);
}

function labelValue(label, value) {
  return `${label}: ${value || '—'}`;
}

async function loadData() {
  isLoading.value = true;

  try {
    await store.dispatch('inboxes/get');
    await store.dispatch('audienceLists/get');
    const response = await LaisMessageTemplatesAPI.get();
    templates.value = (response.data?.payload || []).map(normalizeTemplate);
  } catch (error) {
    useAlert(copy.loadError);
  } finally {
    isLoading.value = false;
  }
}

async function refreshFacebookTemplates({
  silent = false,
  refreshInboxes = false,
} = {}) {
  if (!selectedInboxId.value || isLoadingFacebookTemplates.value) return;

  isLoadingFacebookTemplates.value = true;

  try {
    const response = await InboxesAPI.getWhatsAppTemplates(
      selectedInboxId.value
    );
    facebookTemplates.value = Array.isArray(response.data?.payload)
      ? response.data.payload
      : [];
    hasLoadedFacebookTemplates.value = true;

    if (refreshInboxes) {
      await store.dispatch('inboxes/get');
    }
    if (!silent) useAlert(copy.syncedSyncSuccess);
  } catch (error) {
    if (!silent) {
      useAlert(
        error?.response?.data?.error || error?.message || copy.loadError
      );
    }
  } finally {
    isLoadingFacebookTemplates.value = false;
  }
}

function openFacebookFormModal() {
  facebookForm.value = buildDefaultFacebookForm();
  facebookBodySelection.value = { start: 0, end: 0 };
  showFacebookFormModal.value = true;
}

function closeFacebookFormModal() {
  showFacebookFormModal.value = false;
  facebookForm.value = buildDefaultFacebookForm();
}

function rememberFacebookBodySelection(event) {
  facebookBodySelection.value = {
    start: event.target.selectionStart ?? event.target.value.length,
    end: event.target.selectionEnd ?? event.target.value.length,
  };
}

async function insertFacebookVariable(variable) {
  const input = facebookBodyInput.value;
  const currentValue = facebookForm.value.body || '';
  const selection = facebookBodySelection.value;
  const start = input?.selectionStart ?? selection.start ?? currentValue.length;
  const end = input?.selectionEnd ?? selection.end ?? start;
  const nextValue = `${currentValue.slice(0, start)}${variable.token}${currentValue.slice(end)}`;
  const caretPosition = start + variable.token.length;

  facebookForm.value.body = nextValue;
  facebookBodySelection.value = {
    start: caretPosition,
    end: caretPosition,
  };

  await nextTick();
  input?.focus();
  input?.setSelectionRange(caretPosition, caretPosition);
}

function normalizeFacebookTemplateName() {
  facebookForm.value.name = facebookForm.value.name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

async function createFacebookTemplate() {
  if (
    !selectedInboxId.value ||
    !facebookForm.value.name ||
    !facebookForm.value.body
  ) {
    useAlert(copy.facebookCreateError);
    return;
  }

  isCreatingFacebookTemplate.value = true;

  try {
    const response = await InboxesAPI.createWhatsAppTemplate(
      selectedInboxId.value,
      facebookForm.value
    );
    const createdTemplate = response.data?.payload;

    if (createdTemplate) {
      facebookTemplates.value = [
        createdTemplate,
        ...facebookTemplates.value.filter(template => {
          return template.id !== createdTemplate.id;
        }),
      ];
      hasLoadedFacebookTemplates.value = true;
    }

    closeFacebookFormModal();
    useAlert(copy.facebookCreateSuccess);
    await refreshFacebookTemplates({ silent: true, refreshInboxes: true });
  } catch (error) {
    useAlert(
      error?.response?.data?.error || error?.message || copy.facebookCreateError
    );
  } finally {
    isCreatingFacebookTemplate.value = false;
  }
}

function openCreateModal() {
  editingTemplateId.value = null;
  form.value = buildDefaultForm();
  showFormModal.value = true;
}

function openEditModal(template) {
  editingTemplateId.value = template.id;
  form.value = {
    nome: template.nome,
    categoria: template.categoria || 'remarketing',
    conteudo: template.conteudo,
    ativo: Boolean(template.ativo),
  };
  showFormModal.value = true;
}

function closeFormModal() {
  showFormModal.value = false;
  editingTemplateId.value = null;
  form.value = buildDefaultForm();
}

function openDeleteModal(template) {
  deletingTemplate.value = template;
  showDeleteModal.value = true;
}

function closeDeleteModal() {
  deletingTemplate.value = null;
  showDeleteModal.value = false;
}

async function saveTemplate() {
  if (!form.value.nome.trim() || !form.value.conteudo.trim()) {
    useAlert(copy.saveError);
    return;
  }

  isSaving.value = true;

  const existingVariables =
    templates.value.find(template => template.id === editingTemplateId.value)
      ?.variaveis || {};

  const payload = {
    nome: form.value.nome.trim(),
    categoria: form.value.categoria,
    conteudo: form.value.conteudo.trim(),
    ativo: Boolean(form.value.ativo),
    variaveis: variablesMapFromContent(form.value.conteudo, existingVariables),
  };

  try {
    const response = editingTemplateId.value
      ? await LaisMessageTemplatesAPI.update(editingTemplateId.value, payload)
      : await LaisMessageTemplatesAPI.create(payload);

    const normalized = normalizeTemplate(response.data?.payload || {});

    if (editingTemplateId.value) {
      templates.value = templates.value.map(template =>
        template.id === normalized.id ? normalized : template
      );
      useAlert(copy.updateSuccess);
    } else {
      templates.value = [normalized, ...templates.value];
      useAlert(copy.createSuccess);
    }

    closeFormModal();
  } catch (error) {
    const message =
      error?.response?.data?.errors?.join(', ') ||
      error?.message ||
      copy.saveError;
    useAlert(message);
  } finally {
    isSaving.value = false;
  }
}

async function deleteTemplate() {
  if (!deletingTemplate.value) return;

  isDeleting.value = true;

  try {
    await LaisMessageTemplatesAPI.delete(deletingTemplate.value.id);
    templates.value = templates.value.filter(
      template => template.id !== deletingTemplate.value.id
    );
    useAlert(copy.deleteSuccess);
    closeDeleteModal();
  } catch (error) {
    useAlert(error?.message || copy.deleteError);
  } finally {
    isDeleting.value = false;
  }
}

async function syncInboxTemplates() {
  isSyncing.value = true;
  await refreshFacebookTemplates({ refreshInboxes: true });
  isSyncing.value = false;
}

watch(
  whatsappInboxes,
  inboxes => {
    if (!inboxes.length) {
      selectedInboxId.value = '';
      return;
    }

    const currentExists = inboxes.some(
      inbox => String(inbox.id) === String(selectedInboxId.value)
    );

    if (!currentExists) {
      selectedInboxId.value = String(inboxes[0].id);
    }
  },
  { immediate: true }
);

watch(selectedInboxId, () => {
  facebookTemplates.value = [];
  hasLoadedFacebookTemplates.value = false;
  refreshFacebookTemplates({ silent: true });
});

onMounted(async () => {
  await loadData();
  templateStatusPollingTimer = window.setInterval(() => {
    refreshFacebookTemplates({ silent: true });
  }, 15_000);
});

onUnmounted(() => {
  window.clearInterval(templateStatusPollingTimer);
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="copy.loading"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="operationalSearch"
        :title="copy.pageTitle"
        :description="copy.pageDescription"
        :search-placeholder="copy.operationalSearchPlaceholder"
      >
        <template #count>
          <span class="text-sm text-n-slate-11">
            {{ filteredOperationalTemplates.length }} {{ copy.modelCountLabel }}
          </span>
        </template>
        <template #actions>
          <LaisPrimaryButton @click="openCreateModal">
            {{ copy.newButton }}
          </LaisPrimaryButton>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <section
        class="rounded-[24px] border border-lais-border bg-white p-5 shadow-lais-card sm:p-6"
      >
        <LaisHeader
          :title="copy.syncedTitle"
          :helper="copy.syncedDescription"
        />

        <div
          v-if="!whatsappInboxes.length"
          class="rounded-lais border border-dashed border-lais-border px-5 py-8 text-sm text-lais-text-body"
        >
          {{ copy.syncedEmpty }}
        </div>

        <div v-else class="flex flex-col gap-4">
          <div
            class="grid gap-3 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)_auto_auto]"
          >
            <LaisField :label="copy.syncedInboxLabel">
              <select
                v-model="selectedInboxId"
                class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
              >
                <option
                  v-for="inbox in whatsappInboxes"
                  :key="inbox.id"
                  :value="String(inbox.id)"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </LaisField>

            <LaisField :label="copy.searchPlaceholder">
              <input
                v-model="syncedSearch"
                type="search"
                class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
                :placeholder="copy.syncedSearchPlaceholder"
              />
            </LaisField>

            <div class="flex items-end">
              <LaisPrimaryButton
                :disabled="
                  !selectedInboxId || isSyncing || isLoadingFacebookTemplates
                "
                :is-loading="isSyncing || isLoadingFacebookTemplates"
                @click="syncInboxTemplates"
              >
                {{ copy.syncedSyncButton }}
              </LaisPrimaryButton>
            </div>

            <div class="flex items-end">
              <LaisPrimaryButton
                variant="ghost"
                :disabled="!selectedInboxId || isCreatingFacebookTemplate"
                @click="openFacebookFormModal"
              >
                {{ copy.facebookCreateButton }}
              </LaisPrimaryButton>
            </div>
          </div>

          <p class="text-xs text-lais-text-muted">
            {{ copy.syncedLiveStatus }}
          </p>

          <div
            v-if="filteredSyncedTemplates.length"
            class="grid gap-4 xl:grid-cols-2"
          >
            <article
              v-for="template in filteredSyncedTemplates"
              :key="`${selectedInboxId}-${template.id || template.name}`"
              class="rounded-lais border border-lais-border bg-[#fcfcfd] p-4"
            >
              <div class="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 class="text-base font-semibold text-lais-text">
                    {{ template.name }}
                  </h3>
                  <p
                    class="mt-1 text-xs uppercase tracking-[0.08em] text-lais-text-muted"
                  >
                    {{
                      metadataText(
                        copy.metadataLanguage,
                        template.language || '—'
                      )
                    }}
                  </p>
                </div>
                <span
                  class="rounded-full px-2.5 py-1 text-xs font-semibold"
                  :class="templateStatusClass(template.status)"
                >
                  {{ template.status || 'UNKNOWN' }}
                </span>
              </div>

              <div class="mt-4 space-y-3 text-sm text-lais-text-body">
                <div v-if="componentText(template, 'HEADER')">
                  <p
                    class="text-xs font-semibold uppercase tracking-[0.08em] text-lais-text-muted"
                  >
                    {{ copy.headerLabel }}
                  </p>
                  <p>{{ componentText(template, 'HEADER') }}</p>
                </div>
                <div>
                  <p
                    class="text-xs font-semibold uppercase tracking-[0.08em] text-lais-text-muted"
                  >
                    {{ copy.bodyLabel }}
                  </p>
                  <p>
                    {{
                      componentText(template, 'BODY') ||
                      copy.syncedPreviewFallback
                    }}
                  </p>
                </div>
                <div v-if="componentText(template, 'FOOTER')">
                  <p
                    class="text-xs font-semibold uppercase tracking-[0.08em] text-lais-text-muted"
                  >
                    {{ copy.footerLabel }}
                  </p>
                  <p>{{ componentText(template, 'FOOTER') }}</p>
                </div>
                <div v-if="buttonLabels(template).length">
                  <p
                    class="text-xs font-semibold uppercase tracking-[0.08em] text-lais-text-muted"
                  >
                    {{ copy.buttonsLabel }}
                  </p>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="buttonLabel in buttonLabels(template)"
                      :key="buttonLabel"
                      class="rounded-full bg-n-slate-3 px-2.5 py-1 text-xs text-n-slate-12"
                    >
                      {{ buttonLabel }}
                    </span>
                  </div>
                </div>
              </div>

              <div
                class="mt-4 flex flex-wrap gap-2 text-xs text-lais-text-muted"
              >
                <span
                  class="rounded-full bg-white px-2.5 py-1 border border-lais-border"
                >
                  {{ labelValue(copy.metadataCategory, template.category) }}
                </span>
                <span
                  v-if="template.rejected_reason"
                  class="rounded-full bg-white px-2.5 py-1 border border-lais-border"
                >
                  {{ labelValue('Motivo', template.rejected_reason) }}
                </span>
              </div>
            </article>
          </div>

          <div
            v-else
            class="rounded-lais border border-dashed border-lais-border px-5 py-8 text-sm text-lais-text-body"
          >
            {{ copy.syncedNoTemplates }}
          </div>
        </div>
      </section>

      <section
        class="rounded-[24px] border border-lais-border bg-white p-5 shadow-lais-card sm:p-6"
      >
        <LaisHeader
          :title="copy.operationalTitle"
          :helper="copy.operationalDescription"
        />

        <div class="grid gap-3 md:grid-cols-3">
          <LaisField :label="copy.sectionFilterLabel">
            <select
              v-model="selectedCategory"
              class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option
                v-for="option in categoryOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </LaisField>

          <LaisField :label="copy.sectionStatusLabel">
            <select
              v-model="selectedStatus"
              class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option
                v-for="option in statusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </LaisField>

          <LaisField :label="copy.searchPlaceholder">
            <input
              v-model="operationalSearch"
              type="search"
              class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
              :placeholder="copy.operationalSearchPlaceholder"
            />
          </LaisField>
        </div>

        <div
          v-if="filteredOperationalTemplates.length"
          class="mt-4 grid gap-4 xl:grid-cols-2"
        >
          <article
            v-for="template in filteredOperationalTemplates"
            :key="template.id"
            class="rounded-lais border border-lais-border bg-[#fcfcfd] p-4"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <h3 class="text-base font-semibold text-lais-text">
                  {{ template.nome }}
                </h3>
                <p class="mt-1 text-xs text-lais-text-muted">
                  {{
                    metadataText(
                      template.categoria || '—',
                      formatDate(template.updatedAt)
                    )
                  }}
                </p>
              </div>
              <span
                class="rounded-full px-2.5 py-1 text-xs font-semibold"
                :class="
                  template.ativo
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-slate-100 text-slate-600'
                "
              >
                {{ template.ativo ? copy.active : copy.inactive }}
              </span>
            </div>

            <p
              class="mt-4 whitespace-pre-wrap text-sm leading-6 text-lais-text-body"
            >
              {{ template.conteudo }}
            </p>

            <div class="mt-4">
              <p
                class="text-xs font-semibold uppercase tracking-[0.08em] text-lais-text-muted"
              >
                {{ copy.variablesLabel }}
              </p>
              <div
                v-if="Object.keys(template.variaveis || {}).length"
                class="mt-2 flex flex-wrap gap-2"
              >
                <span
                  v-for="variable in Object.keys(template.variaveis || {})"
                  :key="variable"
                  class="rounded-full bg-white px-2.5 py-1 text-xs border border-lais-border text-lais-text-body"
                >
                  {{ variable }}
                </span>
              </div>
              <p v-else class="mt-2 text-sm text-lais-text-muted">
                {{ copy.variablesEmpty }}
              </p>
            </div>

            <div class="mt-4 flex flex-wrap gap-2">
              <LaisPrimaryButton
                variant="ghost"
                @click="openEditModal(template)"
              >
                {{ copy.editButton }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                @click="openDeleteModal(template)"
              >
                {{ copy.deleteButton }}
              </LaisPrimaryButton>
            </div>
          </article>
        </div>

        <div
          v-else
          class="mt-4 rounded-lais border border-dashed border-lais-border px-5 py-8 text-sm text-lais-text-body"
        >
          {{ copy.operationalEmpty }}
        </div>
      </section>
    </template>
  </SettingsLayout>

  <LaisModal
    :show="showFormModal"
    :title="editingTemplateId ? copy.modalEditTitle : copy.modalCreateTitle"
    max-width-class="max-w-[720px]"
    @close="closeFormModal"
  >
    <div class="flex flex-col gap-4">
      <div class="grid gap-4 md:grid-cols-2">
        <LaisField :label="copy.nameLabel" required>
          <input
            v-model="form.nome"
            type="text"
            class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </LaisField>

        <LaisField :label="copy.categoryLabel" required>
          <select
            v-model="form.categoria"
            class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option
              v-for="option in categoryOptions.filter(
                option => option.value !== 'all'
              )"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </LaisField>
      </div>

      <LaisField
        :label="copy.contentLabel"
        :helper="copy.contentHelper"
        required
      >
        <textarea
          v-model="form.conteudo"
          rows="8"
          class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
        />
      </LaisField>

      <label class="inline-flex items-center gap-3 text-sm text-lais-text-body">
        <input
          v-model="form.ativo"
          type="checkbox"
          class="size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary"
        />
        <span>{{ copy.activeLabel }}</span>
      </label>

      <div class="rounded-lais border border-lais-border bg-[#fcfcfd] p-4">
        <p
          class="text-xs font-semibold uppercase tracking-[0.08em] text-lais-text-muted"
        >
          {{ copy.variablesLabel }}
        </p>
        <div v-if="formVariables.length" class="mt-3 flex flex-wrap gap-2">
          <span
            v-for="variable in formVariables"
            :key="variable"
            class="rounded-full bg-white px-2.5 py-1 text-xs border border-lais-border text-lais-text-body"
          >
            {{ variable }}
          </span>
        </div>
        <p v-else class="mt-3 text-sm text-lais-text-muted">
          {{ copy.variablesEmpty }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <LaisPrimaryButton variant="ghost" @click="closeFormModal">
          {{ copy.cancelButton }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="saveTemplate"
        >
          {{ copy.saveButton }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>

  <LaisModal
    :show="showFacebookFormModal"
    :title="copy.facebookCreateTitle"
    max-width-class="max-w-[720px]"
    @close="closeFacebookFormModal"
  >
    <div class="flex flex-col gap-4">
      <div class="grid gap-4 md:grid-cols-2">
        <LaisField
          :label="copy.nameLabel"
          :helper="copy.facebookNameHelper"
          required
        >
          <input
            v-model="facebookForm.name"
            type="text"
            class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
            @blur="normalizeFacebookTemplateName"
          />
        </LaisField>

        <LaisField :label="copy.facebookLanguageLabel" required>
          <select
            v-model="facebookForm.language"
            class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option
              v-for="option in facebookLanguageOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </LaisField>
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <LaisField :label="copy.facebookCategoryLabel" required>
          <select
            v-model="facebookForm.category"
            class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option
              v-for="option in facebookCategoryOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </LaisField>

        <LaisField
          :label="copy.facebookHeaderLabel"
          :helper="copy.facebookOptionalLabel"
        >
          <input
            v-model="facebookForm.header"
            type="text"
            class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </LaisField>
      </div>

      <div class="rounded-lais border border-lais-border bg-[#fcfcfd] p-4">
        <div class="flex flex-col gap-1">
          <p
            class="text-xs font-semibold uppercase tracking-[0.08em] text-lais-text-muted"
          >
            {{ copy.facebookInterviewVariablesTitle }}
          </p>
          <p class="text-xs text-lais-text-muted">
            {{ copy.facebookInterviewVariablesHelper }}
          </p>
        </div>

        <div class="mt-3 grid gap-2 sm:grid-cols-2">
          <button
            v-for="variable in interviewFacebookVariables"
            :key="variable.key"
            type="button"
            class="flex flex-col items-start gap-1 rounded-lais border border-lais-border bg-white px-3 py-2 text-left text-xs text-lais-text-body transition hover:border-lais-primary hover:bg-lais-primary/5 focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
            @mousedown.prevent
            @click="insertFacebookVariable(variable)"
          >
            <span class="font-medium">{{ variable.label }}</span>
            <span class="text-lais-text-muted">{{ variable.token }}</span>
            <span class="text-lais-text-muted">
              {{ `${copy.facebookCampaignVariableLabel}:` }}
              <code class="text-lais-text-body">
                {{ variable.campaignValue }}
              </code>
            </span>
          </button>
        </div>
      </div>

      <div class="rounded-lais border border-lais-border bg-[#fcfcfd] p-4">
        <div class="flex flex-col gap-1">
          <p
            class="text-xs font-semibold uppercase tracking-[0.08em] text-lais-text-muted"
          >
            {{ copy.facebookVariablesTitle }}
          </p>
          <p class="text-xs text-lais-text-muted">
            {{ copy.facebookVariablesHelper }}
          </p>
        </div>

        <div v-if="facebookVariables.length" class="mt-3 flex flex-wrap gap-2">
          <button
            v-for="variable in facebookVariables"
            :key="variable.key"
            type="button"
            class="flex items-center gap-2 rounded-full border border-lais-border bg-white px-3 py-1.5 text-left text-xs text-lais-text-body transition hover:border-lais-primary hover:bg-lais-primary/5 focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
            :title="
              variable.audiences.length
                ? `${copy.facebookVariableAvailableIn}: ${variable.audiences.join(', ')}`
                : copy.facebookVariablesTitle
            "
            @mousedown.prevent
            @click="insertFacebookVariable(variable)"
          >
            <span class="font-medium">{{ variable.label }}</span>
            <span class="text-lais-text-muted">{{ variable.token }}</span>
          </button>
        </div>
        <p v-else class="mt-3 text-sm text-lais-text-muted">
          {{ copy.facebookVariablesEmpty }}
        </p>
      </div>

      <LaisField
        :label="copy.bodyLabel"
        :helper="copy.facebookBodyHelper"
        required
      >
        <textarea
          ref="facebookBodyInput"
          v-model="facebookForm.body"
          rows="8"
          class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
          @focus="rememberFacebookBodySelection"
          @click="rememberFacebookBodySelection"
          @keyup="rememberFacebookBodySelection"
          @select="rememberFacebookBodySelection"
        />
      </LaisField>

      <LaisField
        :label="copy.facebookFooterLabel"
        :helper="copy.facebookOptionalLabel"
      >
        <input
          v-model="facebookForm.footer"
          type="text"
          class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
        />
      </LaisField>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <LaisPrimaryButton variant="ghost" @click="closeFacebookFormModal">
          {{ copy.cancelButton }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :is-loading="isCreatingFacebookTemplate"
          :disabled="isCreatingFacebookTemplate"
          @click="createFacebookTemplate"
        >
          {{ copy.facebookSubmitButton }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>

  <LaisConfirmModal
    :show="showDeleteModal"
    :title="copy.deleteTitle"
    :description="copy.deleteDescription"
    :confirm-text="copy.deleteConfirm"
    :cancel-text="copy.cancelButton"
    :is-loading="isDeleting"
    icon="i-lucide-trash-2"
    @close="closeDeleteModal"
    @confirm="deleteTemplate"
  />
</template>
